import { useEffect, useRef, useState, useCallback } from 'react';

/**
 * Waveform redaction strip — the upgraded marking surface.
 *
 * wavesurfer.js v7 renders the precomputed peak envelope BOUND TO THE
 * EXISTING media element (`media:` option) — playback, viewer tokens,
 * HLS, and the live-preview engine are untouched; this is purely the
 * visual layer. The Regions plugin supplies the interactions the flat
 * strip lacked: drag on empty waveform to create a redaction,
 * drag/resize existing blocks with edge handles, click to seek, zoom
 * for sub-second precision. Transcript speech spans render as inert
 * tinted regions so silent gaps (= un-transcribed sounds) are visible
 * at a glance.
 *
 * Lazy-loaded like hls.js — non-studio sessions never pay the ~40 KB.
 */

let _wsPromise = null;
function loadWavesurfer() {
  if (_wsPromise) return _wsPromise;
  _wsPromise = Promise.all([
    import('wavesurfer.js'),
    import('wavesurfer.js/dist/plugins/regions.esm.js'),
  ]).then(([ws, rg]) => ({
    WaveSurfer: ws.default || ws,
    RegionsPlugin: rg.default || rg,
  })).catch(err => { _wsPromise = null; throw err; });
  return _wsPromise;
}

const KIND_COLORS = {
  audio_mute: 'rgba(214, 48, 49, 0.35)',
  audio_bleep: 'rgba(230, 126, 34, 0.40)',
  video_region: 'rgba(52, 152, 219, 0.35)',
  video_frame: 'rgba(44, 62, 80, 0.50)',
};
const DRAFT_COLOR = 'rgba(214, 48, 49, 0.30)';
const SPEECH_COLOR = 'rgba(120, 130, 145, 0.14)';

function fmtClock(s) {
  if (!Number.isFinite(s)) return '0:00.0';
  const m = Math.floor(s / 60);
  return `${m}:${(s % 60).toFixed(1).padStart(4, '0')}`;
}

/**
 * Master playhead — the multifunctional marker over the waveform.
 *
 * Tracks playback at 60 fps via rAF + direct DOM writes (the React
 * currentTime prop only ticks ~4 Hz). The head is a control surface:
 *   • live time chip, tinted by whatever active redaction sits under
 *     the playhead (red mute / orange bleep / blue blur / dark frame)
 *   • drag to scrub (live preview gates audio while you sweep)
 *   • ⛔ drops an IN point; the shaded span grows with playback; ⏹
 *     commits IN→playhead as a redaction (length-based kind default)
 *   • wheel on the head nudges ±0.25 s; double-click play/pause
 *   • edge arrows when zoomed playback carries it off-view
 */
function Playhead({ wsRef, wsReady, getMedia, duration, redact }) {
  const lineRef = useRef(null);
  const chipRef = useRef(null);
  const draftRef = useRef(null);
  const [inPoint, setInPoint] = useState(null);
  const inPointRef = useRef(null);
  inPointRef.current = inPoint;
  const itemsRef = useRef(redact.items);
  itemsRef.current = redact.items;

  // Geometry helper: time → x within the visible waveform viewport.
  const geom = useCallback(() => {
    const ws = wsRef.current;
    const wrapper = ws?.getWrapper?.();
    if (!wrapper) return null;
    const scrollEl = wrapper.parentElement || wrapper;
    return {
      scrollEl,
      contentW: Math.max(wrapper.scrollWidth, scrollEl.clientWidth),
      viewW: scrollEl.clientWidth,
    };
  }, [wsRef]);

  // Smooth scroll-following: while playing zoomed-in, keep the playhead
  // centered with per-frame scrollLeft writes (wavesurfer's autoScroll
  // jumps at timeupdate rate — jittery). A manual user scroll pauses
  // following for 1.5s so exploration isn't fought.
  const expectedScrollRef = useRef(null);
  const holdUntilRef = useRef(0);
  useEffect(() => {
    if (!wsReady) return;
    const g = geom();
    if (!g) return;
    const onScroll = () => {
      const exp = expectedScrollRef.current;
      if (exp != null && Math.abs(g.scrollEl.scrollLeft - exp) < 2) return; // ours
      holdUntilRef.current = performance.now() + 1500;
    };
    g.scrollEl.addEventListener('scroll', onScroll, { passive: true });
    return () => g.scrollEl.removeEventListener('scroll', onScroll);
  }, [wsReady, geom]);

  useEffect(() => {
    if (!wsReady) return;
    let raf;
    const tick = () => {
      raf = requestAnimationFrame(tick);
      const el = getMedia();
      const line = lineRef.current;
      const g = geom();
      if (!el || !line || !g || !duration) return;
      const t = el.currentTime;

      if (
        !el.paused && g.contentW > g.viewW + 1
        && performance.now() > holdUntilRef.current
      ) {
        const target = Math.max(0, Math.min(
          g.contentW - g.viewW,
          (t / duration) * g.contentW - g.viewW / 2,
        ));
        if (Math.abs(target - g.scrollEl.scrollLeft) > 0.5) {
          expectedScrollRef.current = target;
          g.scrollEl.scrollLeft = target;
        }
      }

      const x = (t / duration) * g.contentW - g.scrollEl.scrollLeft;
      const xc = Math.max(0, Math.min(g.viewW - 1, x));
      line.style.transform = `translateX(${xc}px)`;
      line.classList.toggle('rdx-ph--off-l', x < -1);
      line.classList.toggle('rdx-ph--off-r', x > g.viewW + 1);

      const chip = chipRef.current;
      if (chip) {
        const txt = fmtClock(t);
        if (chip.dataset.t !== txt) {
          chip.dataset.t = txt;
          chip.textContent = txt;
        }
        const act = itemsRef.current.find(r =>
          r.status === 'active' && t >= r.time_start && t < r.time_end);
        const kind = act ? act.kind : '';
        if (chip.dataset.kind !== kind) chip.dataset.kind = kind;
      }

      const d = draftRef.current;
      if (d) {
        const ip = inPointRef.current;
        if (ip == null) {
          d.style.display = 'none';
        } else {
          const x0 = (Math.min(ip, t) / duration) * g.contentW - g.scrollEl.scrollLeft;
          const x1 = (Math.max(ip, t) / duration) * g.contentW - g.scrollEl.scrollLeft;
          const lo = Math.max(0, x0);
          d.style.display = 'block';
          d.style.left = `${lo}px`;
          d.style.width = `${Math.max(2, Math.min(g.viewW, x1) - lo)}px`;
        }
      }
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [wsReady, duration, getMedia, geom]);

  // Drag-to-scrub (pointer capture on the head, not the buttons).
  const onHeadPointerDown = (e) => {
    if (e.target.closest('button')) return;
    e.preventDefault();
    const move = (me) => {
      const el = getMedia();
      const g = geom();
      if (!el || !g) return;
      const rect = g.scrollEl.getBoundingClientRect();
      const frac = (g.scrollEl.scrollLeft + (me.clientX - rect.left)) / g.contentW;
      el.currentTime = Math.max(0, Math.min(duration, frac * duration));
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    move(e);
  };

  // Wheel-nudge needs a non-passive native listener (React's synthetic
  // wheel can't preventDefault page scroll).
  const headRef = useRef(null);
  useEffect(() => {
    const head = headRef.current;
    if (!head) return;
    const onWheel = (e) => {
      e.preventDefault();
      const el = getMedia();
      if (!el) return;
      el.currentTime = Math.max(0, Math.min(
        duration, el.currentTime + (e.deltaY > 0 ? 0.25 : -0.25)));
    };
    head.addEventListener('wheel', onWheel, { passive: false });
    return () => head.removeEventListener('wheel', onWheel);
  }, [duration, getMedia]);

  // Escape cancels an armed in-point (capture phase — the viewer's own
  // ESC-to-close must not fire).
  useEffect(() => {
    if (inPoint == null) return;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        setInPoint(null);
      }
    };
    window.addEventListener('keydown', onKey, { capture: true });
    return () => window.removeEventListener('keydown', onKey, { capture: true });
  }, [inPoint]);

  const commitInOut = () => {
    const el = getMedia();
    if (!el || inPoint == null) return;
    const a = Math.min(inPoint, el.currentTime);
    const b = Math.max(inPoint, el.currentTime);
    setInPoint(null);
    if (b - a >= 0.2) redact.addRange(a, b, null);
  };

  return (
    <>
      <div className="rdx-ph-draft" ref={draftRef} />
      <div className="rdx-ph" ref={lineRef}>
        <div className="rdx-ph-line" />
        <div
          className="rdx-ph-head"
          ref={headRef}
          onPointerDown={onHeadPointerDown}
          onDoubleClick={() => {
            const el = getMedia();
            if (el) (el.paused ? el.play().catch(() => {}) : el.pause());
          }}
          title="Drag to scrub · wheel to nudge ±0.25s · double-click to play/pause"
        >
          <span className="rdx-ph-chip" ref={chipRef}>0:00.0</span>
          {inPoint == null ? (
            <button
              type="button"
              className="rdx-ph-io"
              onClick={() => {
                const el = getMedia();
                if (el) setInPoint(el.currentTime);
              }}
              title="Drop an IN point here — play/scrub, then ⏹ to mark the span"
            >
              ⛔
            </button>
          ) : (
            <>
              <button
                type="button"
                className="rdx-ph-io rdx-ph-io--armed"
                onClick={commitInOut}
                title={`Mark from ${fmtClock(inPoint)} to here`}
              >
                ⏹
              </button>
              <button
                type="button"
                className="rdx-ph-io"
                onClick={() => setInPoint(null)}
                title="Cancel in-point (Esc)"
              >
                ✕
              </button>
            </>
          )}
        </div>
      </div>
    </>
  );
}

export default function WaveformStrip({ wave, redact, speechSpans = [], onSeek }) {
  const containerRef = useRef(null);
  const wsRef = useRef(null);
  const regionsRef = useRef(null);
  const rebuildingRef = useRef(false);
  const itemsRef = useRef(redact.items);
  itemsRef.current = redact.items;
  const [zoomFactor, setZoomFactor] = useState(1);
  const [wsReady, setWsReady] = useState(false);

  const applyZoom = useCallback((factor) => {
    const ws = wsRef.current;
    const el = containerRef.current;
    if (!ws || !el || !wave.duration) return;
    const fitPxPerSec = el.clientWidth / wave.duration;
    try { ws.zoom(fitPxPerSec * factor); } catch { /* not rendered yet */ }
    setZoomFactor(factor);
  }, [wave.duration]);

  // ── Create / destroy the wavesurfer instance ─────────────────────────
  useEffect(() => {
    if (wave.status !== 'ready' || !containerRef.current) return;
    const mediaEl = redact.mediaEl?.();
    if (!mediaEl) return;
    let destroyed = false;

    loadWavesurfer().then(({ WaveSurfer, RegionsPlugin }) => {
      if (destroyed || !containerRef.current) return;
      const regions = RegionsPlugin.create();
      // The master playhead is the ONLY moving indicator. wavesurfer's
      // native cursor + progress sweep update on ~4Hz timeupdate ticks
      // and its autoScroll jumps in the same steps — running them next
      // to the 60fps playhead reads as jitter. Cursor off, progress =
      // wave color (no sweeping edge), scroll-following handled by the
      // playhead's rAF loop.
      const ws = WaveSurfer.create({
        container: containerRef.current,
        media: mediaEl,
        peaks: [wave.peaks],
        duration: wave.duration,
        height: 64,
        waveColor: '#a8b4c2',
        progressColor: '#a8b4c2',
        cursorWidth: 0,
        interact: true,
        autoScroll: false,
        autoCenter: false,
        plugins: [regions],
      });
      wsRef.current = ws;
      regionsRef.current = regions;

      // Drag on empty waveform → new redaction (kind by length default).
      regions.enableDragSelection({ color: DRAFT_COLOR });

      regions.on('region-created', (region) => {
        if (rebuildingRef.current || region.id.startsWith('rdx-') || region.id.startsWith('speech-')) return;
        // User drag-selection: persist, then drop the draft — the items
        // effect re-adds the canonical block with the server id.
        const { start, end } = region;
        region.remove();
        if (end - start >= 0.15) redact.addRange(start, end, null);
      });

      regions.on('region-updated', (region) => {
        if (!region.id.startsWith('rdx-')) {
          // Speech tints are inert — snap back by rebuilding.
          if (region.id.startsWith('speech-')) rebuildRegions();
          return;
        }
        const id = region.id.slice(4);
        const row = itemsRef.current.find(r => r.id === id);
        if (!row) return;
        const start = Math.max(0, region.start);
        const end = Math.max(start + 0.05, region.end);
        if (Math.abs(row.time_start - start) > 0.01 || Math.abs(row.time_end - end) > 0.01) {
          redact.updateItem(id, { time_start: start, time_end: end });
        }
      });

      regions.on('region-clicked', (region, e) => {
        if (region.id.startsWith('speech-')) {
          e.stopPropagation();
          onSeek?.(region.start);
        }
        // Redaction blocks: let drag/resize own the interaction.
      });

      setWsReady(true);
    }).catch(() => { /* waveform lib failed to load — flat strip remains */ });

    return () => {
      destroyed = true;
      setWsReady(false);
      if (wsRef.current) {
        try { wsRef.current.destroy(); } catch { /* already gone */ }
        wsRef.current = null;
        regionsRef.current = null;
      }
    };
  }, [wave.status, wave.peaks, wave.duration]);  // eslint-disable-line react-hooks/exhaustive-deps

  // ── Sync regions from server state ───────────────────────────────────
  const rebuildRegions = useCallback(() => {
    const regions = regionsRef.current;
    if (!regions) return;
    rebuildingRef.current = true;
    try {
      regions.clearRegions();
      speechSpans.forEach((s, i) => {
        if (!(s.end > s.start)) return;
        regions.addRegion({
          id: `speech-${i}`,
          start: s.start,
          end: s.end,
          color: SPEECH_COLOR,
          drag: false,
          resize: false,
        });
      });
      itemsRef.current.forEach(r => {
        if (r.status !== 'active') return;
        // Black boxes get their own darker tint so blur vs black is
        // readable on the strip, not just in the list chips.
        const color = (r.kind === 'video_region' && r.region?.style === 'black')
          ? 'rgba(44, 62, 80, 0.55)'
          : (KIND_COLORS[r.kind] || KIND_COLORS.audio_mute);
        regions.addRegion({
          id: `rdx-${r.id}`,
          start: r.time_start,
          end: r.time_end,
          color,
          drag: true,
          resize: true,
        });
      });
    } finally {
      rebuildingRef.current = false;
    }
  }, [speechSpans]);

  useEffect(() => {
    if (wsReady) rebuildRegions();
  }, [wsReady, redact.items, rebuildRegions]);

  // Attach hover titles after rebuild (regions render sync).
  useEffect(() => {
    if (!wsReady || !regionsRef.current) return;
    const list = regionsRef.current.getRegions();
    list.forEach(region => {
      if (region.id.startsWith('speech-')) {
        const i = parseInt(region.id.slice(7), 10);
        const text = speechSpans[i]?.text;
        if (text && region.element) region.element.title = text.slice(0, 140);
      } else if (region.id.startsWith('rdx-')) {
        const row = itemsRef.current.find(r => `rdx-${r.id}` === region.id);
        if (row && region.element) {
          const name = {
            audio_bleep: 'Bleep', audio_mute: 'Mute',
            video_frame: 'Frame blackout',
            video_region: row.region?.style === 'black' ? 'Black box' : 'Blur box',
          }[row.kind] || row.kind;
          region.element.title = `${name}${row.label ? ` · ${row.label}` : ''} — drag to move, edges to resize`;
        }
      }
    });
  }, [wsReady, redact.items, speechSpans]);

  return (
    <div className="rdx-wave">
      <div className="rdx-wave-stage">
        <div className="rdx-wave-canvas" ref={containerRef} />
        {wsReady && (
          <Playhead
            wsRef={wsRef}
            wsReady={wsReady}
            getMedia={redact.mediaEl}
            duration={wave.duration}
            redact={redact}
          />
        )}
      </div>
      <div className="rdx-wave-controls">
        <span className="rdx-hint">
          Drag on the waveform to mark · drag a block or its edges to adjust · click to seek
        </span>
        <div className="rdx-legend">
          <span><i className="rdx-lg rdx-lg--mute" />Mute</span>
          <span><i className="rdx-lg rdx-lg--bleep" />Bleep</span>
          <span><i className="rdx-lg rdx-lg--blur" />Blur</span>
          <span><i className="rdx-lg rdx-lg--black" />Black/frame</span>
          <span><i className="rdx-lg rdx-lg--speech" />Speech</span>
        </div>
        <div className="rdx-wave-zoom">
          {[1, 4, 16].map(f => (
            <button
              key={f}
              type="button"
              className={`rdx-zoom-btn ${zoomFactor === f ? 'rdx-zoom-btn--active' : ''}`}
              onClick={() => applyZoom(f)}
              title={f === 1 ? 'Fit whole file' : `Zoom ${f}×`}
            >
              {f === 1 ? 'Fit' : `${f}×`}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
